
import React from 'react';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { FunnelStage } from './types';

interface SalesFunnelProps {
  funnelStages: FunnelStage[];
  metrics: {
    lateStageToGapRatio: string;
    avgWinRatio: string;
    potentialLateStageWinValue: number;
  };
  conversionRate: string;
  handleConversionRateChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  filterLabel: string;
}

const SalesFunnel: React.FC<SalesFunnelProps> = ({
  funnelStages,
  metrics,
  conversionRate,
  handleConversionRateChange,
  filterLabel
}) => {
  const stages = [
    {
      name: "Prospecting stage",
      value: funnelStages[0].value,
      color: "from-purple-400 to-purple-600",
      icon: "🔍",
      widthClass: "w-full"
    },
    {
      name: "Qualification stage",
      value: funnelStages[1].value,
      color: "from-fuchsia-400 to-fuchsia-600",
      icon: "✓",
      widthClass: "w-[calc(100%-40px)]"
    },
    {
      name: "Proposal stage",
      value: funnelStages[2].value,
      color: "from-orange-400 to-orange-600",
      icon: "📄",
      widthClass: "w-[calc(100%-80px)]"
    },
    {
      name: "Negotiation stage",
      value: funnelStages[3].value,
      color: "from-blue-400 to-blue-600",
      icon: "🤝",
      widthClass: "w-[calc(100%-120px)]"
    }
  ];

  return (
    <div className="w-[30%] bg-secondary/80 p-4 border border-primary/20 transition-all duration-500 hover:border-primary/40 shadow-lg flex flex-col rounded py-[11px] relative">
      <h3 className="text-primary-light font-medium mb-1 text-sm">
        Sales Pipeline Analysis {filterLabel}
      </h3>
      
      <div className="flex-1 flex flex-col justify-between">
        <div className="flex flex-col items-center w-full my-1 space-y-1">
          {stages.map((stage, index) => {
            const gradientClass = stage.color;
            return (
              <div key={stage.name} className={`${stage.widthClass} mx-auto`}>
                <div className={`rounded-xl p-px shadow-xl overflow-hidden transform transition-all duration-300 hover:scale-[1.02] bg-gradient-to-r ${gradientClass}`}>
                  <div className="bg-gradient-to-br from-gray-900 to-gray-800 p-2 rounded-xl flex flex-col my-0 py-[11px]">
                    <div className="text-xs text-purple-300 mb-1 text-center">{stage.name}</div>
                    <div className="font-bold text-white text-center text-sm">${stage.value.toLocaleString()}</div>
                    
                    {index === 3 && (
                      <div className="text-amber-300 text-[10px] text-center mt-1">
                        ({metrics.lateStageToGapRatio}X to Revenue Gap)
                      </div>
                    )}
                  </div>
                </div>
                
                {index < stages.length - 1 && (
                  <div className="flex justify-center py-0">
                    <div 
                      className="w-0 h-0 border-l-[8px] border-l-transparent border-t-[8px] border-r-[8px] border-r-transparent" 
                      style={{
                        borderTopColor: index === 0 ? '#a78bfa' : index === 1 ? '#e879f9' : index === 2 ? '#fb923c' : '#60a5fa'
                      }}
                    >
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          
          <div className="mt-3 w-full my-0">
            <Separator className="w-full h-[1px] bg-primary-dark space-y-1 mt-3" />
          </div>
          
          <div className="w-full flex items-center justify-center flex-col space-y-1 mt-2 my-[5px]">
            <div className="flex items-center justify-center">
              <div className="text-[11px] text-purple-300 space-y-1 mt-3">Projecting using historical win ratio:</div>
              <div className="text-amber-300 font-semibold ml-2 text-xs space-y-1 mt-3">{metrics.avgWinRatio}%</div>
            </div>
            
            <div className="flex items-center justify-center">
              <div className="text-[11px] text-purple-300 my-[15px] mx-[13px]">Enter Order to Revenue Conversion Rate:</div>
              <div className="relative ml-2">
                <div className="flex items-center justify-center">
                  <Input 
                    type="text" 
                    value={conversionRate} 
                    onChange={handleConversionRateChange} 
                    className="w-[50px] h-6 p-0 text-center text-xs text-amber-300 bg-transparent border-b border-amber-300/50 focus:border-amber-300 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0" 
                  />
                  <span className="flex items-center justify-center text-amber-300 text-xs h-6 ml-0.5">%</span>
                </div>
              </div>
            </div>
            
            <div className={`rounded-xl p-px shadow-xl overflow-hidden transform transition-all duration-300 hover:scale-[1.02] bg-gradient-to-r from-emerald-400 to-emerald-600 w-[calc(100%-150px)] mx-auto`}>
              <div className="bg-gradient-to-br from-gray-900 to-gray-800 p-2 rounded-xl flex flex-col my-0 mx-0 px-[6px] py-[2px]">
                <div className="text-xs text-emerald-300 text-center">Potential late stage win revenue</div>
                <div className="font-bold text-white text-center text-sm">
                  ${Math.round(metrics.potentialLateStageWinValue).toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesFunnel;
